import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  colors: {
    background: string;
    surface: string;
    surfaceHover: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    heading: string;
    primary: string;
    primaryHover: string;
    border: string;
    borderLight: string;
    cardBg: string;
    inputBg: string;
    shadow: string;
  };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const lightColors = {
  background: '#F7F8FA',
  surface: '#FFFFFF',
  surfaceHover: '#F0EEFF',
  text: '#464452',
  textSecondary: '#8B8A92',
  textTertiary: '#B4B3BA',
  heading: '#2D1F7B',
  primary: '#594BFF',
  primaryHover: '#8B7FFF',
  border: '#E2DFEA',
  borderLight: '#F0EDF7',
  cardBg: '#FFFFFF',
  inputBg: '#FFFFFF',
  shadow: 'rgba(89, 75, 255, 0.12)',
};

const darkColors = {
  background: '#0A0A0F',
  surface: '#16151D',
  surfaceHover: '#1F1E29',
  text: 'rgba(255, 255, 255, 0.95)',
  textSecondary: 'rgba(255, 255, 255, 0.70)',
  textTertiary: 'rgba(255, 255, 255, 0.45)',
  heading: 'rgba(255, 255, 255, 0.98)',
  primary: '#8B7FFF',
  primaryHover: '#A89FFF',
  border: 'rgba(255, 255, 255, 0.08)',
  borderLight: 'rgba(255, 255, 255, 0.05)',
  cardBg: '#16151D',
  inputBg: '#0F0E16',
  shadow: 'rgba(0, 0, 0, 0.5)',
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('petso-theme') as Theme;
    return savedTheme || 'light';
  });

  useEffect(() => {
    localStorage.setItem('petso-theme', theme);
    
    // Update document root class for global styles
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const colors = theme === 'light' ? lightColors : darkColors;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}